<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { useBreadcrumbsState, type BreadcrumbItem } from '@/composables/useBreadcrumbs'

interface BreadcrumbProps {
  /** Start the trail with a link to the dashboard */
  showHome?: boolean
  homeLabel?: string
  homePath?: string
}

const props = withDefaults(defineProps<BreadcrumbProps>(), {
  showHome: true,
  homeLabel: 'Dashboard',
  homePath: '/home',
})

const { breadcrumbs: routeCrumbs } = useBreadcrumbsState()

const items = computed<BreadcrumbItem[]>(() => {
  const crumbs = routeCrumbs.value
  if (!crumbs.length) return []
  const startsAtHome = crumbs[0]?.label === props.homeLabel || crumbs[0]?.path === props.homePath
  return props.showHome && !startsAtHome ? [{ label: props.homeLabel, path: props.homePath }, ...crumbs] : crumbs
})

// On small screens the trail becomes one "back" link to the page above this one
const parent = computed(() => [...items.value].slice(0, -1).reverse().find(item => item.path))
</script>

<template>
  <nav v-if="items.length" class="bc" aria-label="Breadcrumb">
    <ol class="bc-list">
      <li v-for="(item, index) in items" :key="`${index}-${item.label}`" class="bc-item">
        <RouterLink v-if="item.path && index < items.length - 1" :to="item.path" class="bc-link">
          <i v-if="index === 0 && showHome" class="fa fa-home" aria-hidden="true"></i>
          <span :class="{ 'visually-hidden': index === 0 && showHome }">{{ item.label }}</span>
        </RouterLink>
        <span v-else class="bc-current" :aria-current="index === items.length - 1 ? 'page' : undefined">{{ item.label }}</span>
        <i v-if="index < items.length - 1" class="fa fa-chevron-right bc-sep" aria-hidden="true"></i>
      </li>
    </ol>

    <RouterLink v-if="parent?.path" :to="parent.path" class="bc-back">
      <i class="fa fa-arrow-left" aria-hidden="true"></i>
      <span>{{ parent.label }}</span>
    </RouterLink>
  </nav>
</template>

<style scoped>
.bc {
  display: flex;
  align-items: center;
  min-height: 44px;
  font-size: 13px;
}

.bc-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.bc-item {
  display: inline-flex;
  align-items: center;
  min-width: 0;
}

.bc-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border-radius: 6px;
  color: var(--ui-muted);
  font-weight: 500;
  text-decoration: none;
}

.bc-item:first-child .bc-link {
  margin-left: -6px;
}

.bc-link:hover {
  background: var(--ui-mist);
  color: var(--ui-red-dark);
}

.bc-link:focus-visible,
.bc-back:focus-visible {
  outline: 2px solid var(--ui-red-dark);
  outline-offset: 1px;
}

.bc-link .fa-home {
  font-size: 14px;
}

.bc-current {
  max-width: 420px;
  padding: 4px 6px;
  overflow: hidden;
  color: var(--ui-ink);
  font-weight: 700;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.bc-sep {
  margin: 0 2px;
  font-size: 9px;
  color: var(--ui-muted);
  opacity: 0.5;
}

.bc-back {
  display: none;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  padding: 4px 0;
  color: var(--ui-ink);
  font-weight: 600;
  text-decoration: none;
}

.bc-back i {
  color: var(--ui-red-dark);
}

/* Phones: one back link instead of the whole trail */
@media (max-width: 640px) {
  .bc-list {
    display: none;
  }

  .bc-back {
    display: inline-flex;
  }
}
</style>
